import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  BaseSiteService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { map, Subscription, take } from 'rxjs';
import { RecaptchaService } from 'src/app/shared/services/recaptcha.service';
import { CustomRegistrationService } from '../../custom-registration.service';
import {
  globalErrorMsgEng,
  globalErrorMsgFr,
  siteLanguagesEng,
} from '../../registration.constants';

declare const grecaptcha: any;

declare global {
  interface Window {
    onRecaptchaLoad: () => void;
  }
}

interface loggedInUserData {
  accountNumber: any;
  postalCode: any;
}

@Component({
  selector: 'app-account-information',
  templateUrl: './account-information.component.html',
  standalone: false,
})
export class AccountInformationComponent implements OnInit, OnDestroy, AfterViewInit {
  accountInfoForm: FormGroup;
  errorMsg = false;
  billToShipError = false;
  errorMessageText: string = globalErrorMsgEng;
  accountInfoSub: Subscription;
  captchaResp: string;
  resetPasswordEmailSent = false;
  existingUserContinue = false;
  emailRegexExp =
    '^([A-Za-z0-9\\._-]+)@([A-Za-z0-9\\._-]+)\\.([A-Za-z\\.]{2,8})(\\.[A-Za-z\\.]{2,8})?$';
  infoMsg: boolean;
  isIndirectB2BUnitValidated = false;
  validatedUserName: any;
  bannerMessage: any;
  loggedInUserData: loggedInUserData;
  isloggedIn: any;
  accountFormValue: any;
  captchaTriggered = false
  subRecaptcha: Subscription;
  recaptchaEnabled = false;
  siteKey: string | null = null;
  widgetId: any;

  constructor(
    private readonly accountRegService: CustomRegistrationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly languageService: LanguageService,
    private readonly globalMessage: GlobalMessageService,
    private user: UserAccountFacade,
    private router: Router,
    protected baseSiteService: BaseSiteService,
    private readonly recaptchaService: RecaptchaService
  ) { }

  ngAfterViewInit(): void {
    console.log("ngAfterViewInit")
    if (window['grecaptcha'] && window['grecaptcha'].enterprise) {
      this.loadRecaptcha();
    }
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    const maxlengthChar = 20;
    const maxlengthSeven = 7;
    this.languageService.getActive().subscribe((langRes) => {
      this.errorMessageText =
        langRes === siteLanguagesEng ? globalErrorMsgEng : globalErrorMsgFr;
    });
    this.accountInfoForm = new FormGroup({
      accountNumber: new FormControl('', [
        Validators.required,
        Validators.pattern('[0-9]+$'),
        Validators.maxLength(maxlengthChar),
      ]),
      postalCode: new FormControl('', [
        Validators.required,
        Validators.pattern('^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] [0-9][A-Z][0-9]$'),
        Validators.maxLength(maxlengthSeven),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(this.emailRegexExp),
      ]),
    });
    this.userDetection();
    this.subRecaptcha = this.recaptchaService
      .isEnabled$()
      .subscribe((enabled) => {
        this.recaptchaEnabled = enabled;
        this.siteKey = this.recaptchaService.getSiteKey();
        if (enabled) {
          this.accountInfoForm.addControl(
            'recaptchaReactive',
            new FormControl('', Validators.required)
          );
        }
      });
    window['onRecaptchaLoad'] = () => this.loadRecaptcha();
  }

  private loadRecaptcha() {
    const container = document.getElementById('recaptcha-container');
    if (!container || !window['grecaptcha'] || !grecaptcha.enterprise.render) {
      console.error('Recaptcha not ready');
      return;
    }

    this.widgetId = grecaptcha.enterprise.render('recaptcha-container', {
      sitekey: this.siteKey,
      theme: 'light',
      size: 'normal',
      callback: (token: string) => this.onCaptchaSuccess(token)
    });
  }

  private onCaptchaSuccess(token: string) {
    //console.log('Captcha success token:', token);
    this.accountInfoForm.patchValue({ recaptchaReactive: token });
  }

  validateAccountInfo() {
    this.accountInfoForm.markAllAsTouched();
    if (this.accountInfoForm.valid) {
      if (this.isIndirectB2BUnitValidated) {
        this.validateIndirectAccountInAkamai();
      } else {
        if (this.existingUserContinue) {
          this.reinitializeFormData(true);
          this.errorMsg = false;
          const accountInfo = {
            header: 'userInformation',
            form: this.accountInfoForm,
            akamaiRegistered: { registeredAkamaiUser: false },
            isLoggedIn: !!this.isloggedIn,
          };
          this.accountRegService.sectionChange.next(accountInfo);
        }
        this.validateIndirectAccountInfo();
      }
    } else {
      window.scroll(0, 0);
      this.globalMessage.add(
        this.errorMessageText,
        GlobalMessageType.MSG_TYPE_ERROR
      );
    }
  }

  validateIndirectAccountInfo() {
    this.reinitializeFormData();
    if (!this.captchaTriggered) {
      this.accountInfoSub = this.accountRegService
        .postValidateIndirectAccountInfo(
          this.accountFormValue,
          this.captchaResp
        )
        .subscribe(
          (res) => {
            this.captchaTriggered = true;
            if (res.existingUserCheck) {
              this.validatedUserName = { userName: res?.userName };
              this.accountInfoForm.addControl(
                'password',
                new FormControl('', Validators.required)
              );
              this.isIndirectB2BUnitValidated = true;
              this.infoMsg = true;
              this.cdr.detectChanges();
            }
            if (!res.existingUserCheck) {
              this.existingUserContinue = !res.existingUserCheck;
              this.accountInfoForm.addControl(
                'confirmEmail',
                new FormControl('', [
                  Validators.required,
                  Validators.pattern(this.emailRegexExp),
                ])
              );
              this.accountInfoForm.addValidators(this.emailMatchValidator);
              this.cdr.detectChanges();
            }
          },
          (error) => {
            if (!!error) {
              this.accountErrors(error);
              this.generateErrors();
            }
          }
        );
    }
  }

  validateIndirectAccountInAkamai() {
    if (this.accountInfoForm.valid) {
      const isRegisteredAkamaiUser = { registeredAkamaiUser: true };
      const formvalue = {
        ...this.accountFormValue,
        ...isRegisteredAkamaiUser,
        ...this.validatedUserName,
      };
      this.reinitializeFormData();
      this.accountInfoSub = this.accountRegService
        .postValidateIndirectAccountInAkamai(formvalue)
        .subscribe(
          (res) => {
            this.reinitializeFormData(true);
            this.errorMsg = false;
            const accountInfo = {
              header: 'userInformation',
              form: this.accountInfoForm,
              akamaiRegistered: {
                registeredAkamaiUser: true,
                uuid: res.uuid,
                ...this.validatedUserName,
              },
              isLoggedIn: this.isloggedIn,
            };
            this.accountRegService.sectionChange.next(accountInfo);
          },
          (error) => {
            if (!!error) {
              this.checkIndirectErrorsFromAkamai(error);
              this.generateErrors();
              this.accountInfoForm.patchValue({ recaptchaReactive: null });
            }
          }
        );
    } else {
      window.scroll(0, 0);
      this.globalMessage.add(
        this.errorMessageText,
        GlobalMessageType.MSG_TYPE_ERROR
      );
    }
  }

  checkIndirectErrorsFromAkamai(error) {
    if (!!error?.error?.errors && error?.error?.errors.length > 0) {
      error.error.errors[0].message === 'register.janrain.invalid.password'
        ? this.generateErrorMessages('formValidationMsgPassword')
        : this.generateErrorMessages('SanofiAkamaiWebServiceError');
    }
  }

  sendResetPasswordEmail() {
    const email = this.accountInfoForm.get('email').value;
    if (!!email) {
      this.accountRegService.postSendResetPasswordEmail(email).subscribe(
        (res) => {
          this.resetPasswordEmailSent = true;
          window.scroll(0, 0);
          this.cdr.detectChanges();
        },
        (error) => {
          this.resetPasswordEmailSent = false;
          this.cdr.detectChanges();
        }
      );
    }
  }

  resolved(captchaResponse: string) {
    this.captchaResp = captchaResponse;
  }

  emailMatchValidator(frm: FormGroup) {
    if (!!frm) {
      if (frm.get('email').value !== frm.get('confirmEmail').value) {
        return { emailNotMatch: true };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  generateErrors() {
    window.scroll(0, 0);
    this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
    this.globalMessage.add(
      this.errorMessageText,
      GlobalMessageType.MSG_TYPE_ERROR
    );
    this.cdr.detectChanges();
  }

  accountErrors(error) {
    if (!!error?.error?.errors && error?.error?.errors.length > 0) {
      if (error?.error?.errors[0].type === 'SanofiAkamaiWebServiceError') {
        this.generateErrorMessages('SanofiAkamaiWebServiceError');
      } else {
        error?.error?.errors[0].message ===
          'b2bCustomer.register.accountNumber.invalidType'
          ? this.generateErrorMessages('billToShipToValidationMsg')
          : error?.error?.errors[0].message ===
            'b2bCustomer.register.email.exists'
            ? this.generateErrorMessages('emailExists')
            : this.generateErrorMessages('formValidationMsg');
      }
    }
  }

  generateErrorMessages(message) {
    if (message === 'billToShipToValidationMsg') this.billToShipError = true;
    this.bannerMessage = 'userRegistration.accountInformation.' + message;
    this.errorMsg = true;
  }

  userDetection() {
    this.user
      .get()
      .pipe(
        take(2),
        map((user) =>
          !!user ? this.confirmUserData(user) : this.redirectUser()
        )
      )
      .subscribe();
  }

  confirmUserData(user) {
    if (user)
      this.accountRegService
        .getAccountData(user.uid, user.orgUnit.uid)
        .pipe(
          take(1),
          map((acct) => this.prepopulateFields(acct))
        )
        .subscribe();
  }

  redirectUser() {
    if (this.router.url.includes('/my-account')) this.router.navigateByUrl('/');
  }

  prepopulateFields(acct) {
    this.isloggedIn = true;
    this.loggedInUserData = {
      accountNumber: acct.uid,
      postalCode: acct.payerAddress.postalCode,
    };
    this.accountInfoForm.controls.accountNumber.setValue(acct.uid);
    this.accountInfoForm.controls.postalCode.setValue(
      acct.payerAddress.postalCode
    );
    this.accountInfoForm.controls.accountNumber.disable();
    this.accountInfoForm.controls.postalCode.disable();
  }

  reinitializeFormData(shiftPage?) {
    if (!!this.loggedInUserData) {
      this.accountFormValue = this.accountInfoForm.getRawValue();
      for (let key in this.loggedInUserData) {
        if (
          this.accountFormValue.hasOwnProperty(key) &&
          this.accountFormValue[key] !== this.loggedInUserData[key]
        ) {
          this.accountFormValue[key] = this.loggedInUserData[key];
          this.accountInfoForm.controls[key].patchValue(
            this.loggedInUserData[key]
          );
        }
      }
    } else {
      this.accountFormValue = this.accountInfoForm.value;
    }
    if (shiftPage) {
      this.accountInfoForm.controls.accountNumber.enable();
      this.accountInfoForm.controls.postalCode.enable();
    }
  }

  ngOnDestroy() {
    this.accountInfoSub?.unsubscribe();
    this.subRecaptcha?.unsubscribe();
  }
}
